import { useEffect } from "react";

const SITE_NAME = "Hays Holdings Inc";
const BASE_URL = "https://www.haysholdingsinc.com";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(schemas) {
  // Remove previously injected JSON-LD, then add the current page's set.
  document.head
    .querySelectorAll('script[data-seo-jsonld="1"]')
    .forEach((el) => el.remove());
  schemas.filter(Boolean).forEach((data) => {
    const el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-jsonld", "1");
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
  });
}

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "Hays Holdings",
  url: BASE_URL,
  description:
    "Hays Holdings is a national product distribution company helping brands, manufacturers, and CPG companies expand into U.S. retail through wholesale and retail distribution.",
  email: "nathan@haysholdingsinc.com",
  telephone: "+1-657-768-2609",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Anaheim",
    addressRegion: "CA",
    addressCountry: "US",
  },
  areaServed: { "@type": "Country", name: "United States" },
  knowsAbout: [
    "Product Distribution",
    "National Distribution",
    "Wholesale Distribution",
    "Retail Distribution",
    "Consumer Goods Distribution",
    "CPG Distribution",
    "Amazon Marketplace Growth",
    "Brand Distribution",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "nathan@haysholdingsinc.com",
    telephone: "+1-657-768-2609",
    areaServed: "US",
    availableLanguage: "English",
  },
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  url: BASE_URL,
  name: SITE_NAME,
  description:
    "National product distribution company and distribution partner for brands and manufacturers across the United States.",
  publisher: { "@id": `${BASE_URL}/#organization` },
  inLanguage: "en-US",
};

export const SERVICE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Product Distribution & National Sales Distribution",
  serviceType: "Wholesale & Retail Product Distribution",
  provider: { "@id": `${BASE_URL}/#organization` },
  areaServed: { "@type": "Country", name: "United States" },
  audience: {
    "@type": "Audience",
    audienceType:
      "Brands, manufacturers, importers, wholesalers, private-label and consumer product companies seeking a U.S. distribution partner",
  },
  description:
    "Nationwide product distribution services that help brands and manufacturers expand into new retail markets, scale wholesale and retail distribution, and grow sales across the United States.",
};

function buildBreadcrumb(path, title) {
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` }];
  if (path && path !== "/") {
    items.push({
      "@type": "ListItem",
      position: 2,
      name: title || path.replace("/", ""),
      item: `${BASE_URL}${path}`,
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

// Backend SEO manager — title, meta, canonical, robots, OG/Twitter, structured data.
export function useSeo({
  title,
  description,
  path = "/",
  keywords,
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  extraJsonLd = [],
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    const url = `${BASE_URL}${path}`;
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", robots);
    upsertMeta("name", "googlebot", robots);
    upsertLink("canonical", url);

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "en_US");
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);

    const indexable = robots.includes("index") && !robots.includes("noindex");
    const webpage = indexable
      ? {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${url}#webpage`,
          url,
          name: fullTitle,
          description,
          isPartOf: { "@id": `${BASE_URL}/#website` },
          about: { "@id": `${BASE_URL}/#organization` },
          inLanguage: "en-US",
        }
      : null;

    const schemas = indexable
      ? [
          ORGANIZATION_SCHEMA,
          WEBSITE_SCHEMA,
          webpage,
          buildBreadcrumb(path, title),
          ...extraJsonLd,
        ]
      : [];
    setJsonLd(schemas);
  }, [title, description, path, keywords, robots, extraJsonLd]);
}
